import { UserCirclePlusIcon } from "@phosphor-icons/react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type * as yup from "yup";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { useRegistration } from "../../hooks/use-registration";
import { useGetConfig } from "../../store/use-config";
import { Card } from "../card";
import { SideCart } from "../cart/side-cart";
import { Error } from "../error";
import { TextField } from "../form-fields/text-field";
import { useRegistrationLogic } from "./use-registration-logic";
import type { useValidationSchema } from "./validation-schema";

export type StandardRegistrationFormValues = yup.InferType<ReturnType<typeof useValidationSchema>>;

const FORM_ID = "registration-form";

export const RegistrationForm = () => {
  const { t } = useTranslation();
  const config = useGetConfig();
  const {
    formMethods,
    handleSimplifyRegistration,
    simplifyRegistrationError,
    isLoading: isCreateSessionLoading,
  } = useRegistrationLogic();
  const {
    handleRegistration,
    handleRegistrationInvalidSubmit,
    registerError,
    createOrderError,
    isLoading: isRegisterLoading,
  } = useRegistration();
  const isLoading = isCreateSessionLoading || isRegisterLoading;

  return (
    <FormProvider {...formMethods}>
      <SidebarLayout>
        <Card>
          <div className="flex items-center gap-4">
            <UserCirclePlusIcon className="size-8" />
            <h2 className="font-semibold text-xl">
              {t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.CREATE_ACCOUNT")}
            </h2>
          </div>
          <p className="mt-2 mb-6 text-gray-500 text-sm md:mb-8">
            {t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.CREATE_ACCOUNT_DESCRIPTION")}
          </p>
          <form id={FORM_ID} onSubmit={formMethods.handleSubmit(handleRegistration, handleRegistrationInvalidSubmit)}>
            <div className="flex flex-col gap-6 md:grid md:grid-cols-2">
              <TextField
                ref={formMethods.register}
                name="email"
                required
                label={t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.EMAIL")}
                error={formMethods.errors.email?.message}
              />
              <TextField
                ref={formMethods.register}
                name="emailConfirmation"
                required
                label={t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.EMAIL_CONFIRMATION")}
                error={formMethods.errors.emailConfirmation?.message}
              />
              <div className="col-span-2">
                <TextField
                  ref={formMethods.register}
                  name="password"
                  type="password"
                  required
                  helperText={t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.PASSWORD_HELPER_TEXT")}
                  label={t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.PASSWORD")}
                  error={formMethods.errors.password?.message}
                />
                {/* Guest checkout is a low-emphasis alternative (no account benefits), so it sits right under
                    the password helper as a muted inline link rather than a prominent centered CTA. */}
                {config.simplifiedRegistration && !isLoading && (
                  <button
                    type="button"
                    onClick={handleSimplifyRegistration}
                    className="mt-2 cursor-pointer text-muted-foreground text-sm transition-colors hover:text-foreground"
                  >
                    {t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.SIMPLIFY_REGISTRATION_BUTTON_LABEL")}
                  </button>
                )}
              </div>
            </div>
          </form>
        </Card>
        <SideCart
          readOnly
          submitFormId={FORM_ID}
          buttonLabel={t("GROUP_ACTIONS_ORDERING.STANDARD_REGISTRATION_FORM.SUBMIT_BUTTON_LABEL")}
          ctaLoading={isLoading}
          appendedContent={
            <Error error={registerError || createOrderError || simplifyRegistrationError} className="mt-2" />
          }
        />
      </SidebarLayout>
    </FormProvider>
  );
};
